import { RecommendedProduct } from '../types';
import { API_BASE } from '../apiBase';

/**
 * Calls the backend product recommender API to find the most relevant product
 * based on the semantic meaning of a search sentence.
 * @param searchText - The sentence describing the desired product context.
 * @returns A promise that resolves to the single most relevant product.
 */
function sanitizeSearchText(raw: string): string {
  if (!raw) return '';
  const txt = String(raw).trim();
  return txt.replace(/\s+/g, ' ');
}

function isClearlyUiStatus(text: string): boolean {
  const lowered = text.toLowerCase();
  return (
    lowered.includes('processing slides') ||
    lowered.includes('images attached') ||
    lowered.includes('hide debug') ||
    lowered.includes('show debug') ||
    lowered.startsWith('🧠') ||
    lowered.startsWith('🎛️') ||
    lowered.startsWith('📐') ||
    lowered.startsWith('📋') ||
    lowered.startsWith('🖼️') ||
    lowered.startsWith('🎨') ||
    lowered.startsWith('✅') ||
    lowered.startsWith('⚠️')
  );
}

export async function getRelevantProduct(searchText: string): Promise<RecommendedProduct> {
  try {
    const clean = sanitizeSearchText(searchText);
    if (!clean || clean.length < 10 || isClearlyUiStatus(clean)) {
      throw new Error(`Rejected non-semantic search text: "${clean?.slice(0, 80) || 'empty'}"`);
    }
    console.log(`[ProductRecommender] Vector query (sanitized): ${clean.slice(0, 160)}`);
    // Get 4 products from vector search for Gemini to choose from
    const response = await fetch(`${API_BASE}/api/get-relevant-products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Get 4 products for Gemini to select the best one
      body: JSON.stringify({ searchText: clean, limit: 4 }),
      credentials: 'include',
    });

    const responseText = await response.text();

    if (!response.ok) {
      console.error('Product recommender API error:', responseText);
      throw new Error(`The product recommender API failed: ${response.status} ${responseText}`);
    }

    if (!responseText || responseText.trim() === '') {
      console.error('Empty response from product recommender API');
      throw new Error('Product recommender API returned empty response');
    }

    let results: RecommendedProduct[];
    try {
      results = JSON.parse(responseText);
    } catch (parseError: any) {
      console.error('Failed to parse product recommender response:', responseText);
      throw new Error(`Invalid JSON response from product recommender: ${parseError.message}`);
    }

    if (!results || results.length === 0) {
      throw new Error('No relevant products were found for your sentence. Please try a different sentence or check your product database.');
    }

    // Check if the backend returned a structured error message
    const firstResult = results[0];
    if (firstResult.name === 'Error retrieving products' && firstResult.description) {
      throw new Error(`Error from product database: ${firstResult.description}`);
    }

    // Frontend fallback: use the top product from the backend ranking
    return results[0];
  } catch (error: any) {
    console.error("Error calling product recommender service:", error);
    // Re-throw a more user-friendly error.
    if (error.message.startsWith('Error from product database:')) {
      throw error; // Re-throw the specific error from the backend
    }
    throw new Error('Could not connect to the product recommender service or the query was invalid.');
  }
}

export async function getRelevantProducts(searchText: string, limit: number = 4): Promise<RecommendedProduct[]> {
  const clean = sanitizeSearchText(searchText);
  if (!clean || clean.length < 4) return [];
  console.log(`getRelevantProducts - searchText: ${clean}, limit: ${limit}`);
  const response = await fetch(`${API_BASE}/api/get-relevant-products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ searchText: clean, limit })
  });

  const responseText = await response.text();

  if (!response.ok) {
    console.warn('getRelevantProducts failed', responseText);
    return [];
  }

  if (!responseText || responseText.trim() === '') {
    console.warn('Empty response from getRelevantProducts');
    return [];
  }

  let results: RecommendedProduct[];
  try {
    results = JSON.parse(responseText);
  } catch (parseError) {
    console.error('Failed to parse getRelevantProducts response:', responseText);
    return [];
  }

  console.log('getRelevantProducts - Formatted Results:', results);
  return Array.isArray(results) ? results.slice(0, limit) : [];
}
